import { MouseEvent, ReactNode } from 'react'
import classNames from 'classnames/bind'
import { isEmpty } from 'ramda'

import SortArrowControl from './sort-arrow-control'
import styles from './th.module.scss'

/**
 * UI COMPONENTS v2
 *
 * Строка таблицы
 * ------
 * Семантически верная таблица. Строка таблицы (tr)
 */

const cx = classNames.bind(styles)

export type ThProps = {
  children: ReactNode
  className?: string
  contentClassName?: string
  stripStyles?: boolean
  stripPadding?: boolean
  alignLeft?: boolean
  alignCenter?: boolean
  alignRight?: boolean
  verticalAlignTop?: boolean
  verticalAlignBottom?: boolean
  colSpan?: number
  rowSpan?: number
  id?: string
  hidden?: boolean
  invisibility?: boolean
  sortKey?: string
  sortDirection?: 'up' | 'down'
  scope?: 'col' | 'row' | 'colgroup' | 'rowgroup'
  headers?: string
  sortDummy?: boolean
  style?: { [key: string]: string }
  borderBottom?: boolean
  borderShadowBottom?: boolean
  borderTopCompensate?: boolean
  borderLeft?: boolean
  borderRight?: boolean
  bgFilled?: boolean
  bgColor?: string
  ignoreFirstChild?: boolean
  ignoreLastChild?: boolean
  width?: number
  minWidth?: number
  onSortArrowClick?: (direction: 'up' | 'down', key: string) => void
  onMouseEnter?: (e: MouseEvent) => void
  onMouseLeave?: (e: MouseEvent) => void
  ignoreMouseEvents?: boolean
}

const Th = ({
  children,
  className,
  contentClassName,
  stripStyles,
  stripPadding,
  alignLeft,
  alignCenter,
  alignRight,
  verticalAlignTop,
  verticalAlignBottom,
  colSpan,
  rowSpan,
  id,
  hidden,
  invisibility,
  sortKey,
  sortDirection,
  scope,
  headers,
  sortDummy,
  style,
  borderBottom,
  borderShadowBottom,
  borderTopCompensate,
  borderLeft,
  borderRight,
  bgFilled,
  bgColor,
  ignoreFirstChild,
  ignoreLastChild,
  width,
  minWidth,
  onSortArrowClick,
  onMouseEnter,
  onMouseLeave,
  ignoreMouseEvents,
}: ThProps) => {
  const rootClassName = cx(
    {
      root: !stripStyles,
      rootPadding: !stripStyles && !stripPadding,
      stripPadding,
      alignLeft,
      alignCenter,
      alignRight,
      verticalAlignTop,
      verticalAlignBottom,
      hidden,
      invisibility,
      borderBottom,
      borderShadowBottom,
      borderTopCompensate,
      borderLeft,
      borderRight,
      bgFilled,
      ignoreFirstChild,
      ignoreLastChild,
    },
    className,
  )

  const noneSortableClassName = cx({ sortDummy }, contentClassName)
  const sortableClassName = cx(styles.content, contentClassName)

  if (hidden) {
    return null
  }

  const styleOverall = {
    ...(style ?? {}),
    ...(width ? { width, maxWidth: width } : {}),
    ...(minWidth ? { minWidth } : {}),
    ...(bgColor ? { backgroundColor: bgColor } : {}),
  }

  const mouseEvents = ignoreMouseEvents ? {} : { onMouseEnter, onMouseLeave }

  const props = {
    id,
    colSpan,
    rowSpan,
    scope,
    headers,
    style: isEmpty(styleOverall) ? undefined : styleOverall,
    ...mouseEvents,
  }

  return (
    <th className={rootClassName} {...props}>
      {sortKey && !sortDummy ? (
        <div className={sortableClassName}>
          {children}
          <SortArrowControl
            sortKey={sortKey}
            sortDirection={sortDirection}
            onClick={onSortArrowClick}
          />
        </div>
      ) : (
        <div className={noneSortableClassName}>{children}</div>
      )}
    </th>
  )
}

export default Th
